import therapistSchedulingServices from '../../../services/apiServices/therapist-scheduling-services/therapist-scheduling-services';
import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Typography,
  Box,
  Button,
} from '@mui/material';
import { AxiosResponse } from 'axios';

interface ScheduleDialogProps {
  providerUUID: string;
  dateString: string;
  open: boolean;
  onClose: () => void;
  sortOrder?: string;
}

interface SchedulePrintPDFResponse {
  date: string;
  code: string;
  message: string;
  data: any | null;
  path: string;
  requestId: string;
  version: string;
}

const SchedulePrintDialog: React.FC<ScheduleDialogProps> = ({
  providerUUID,
  dateString,
  open,
  onClose,
  sortOrder
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPdf = useCallback(() => {
    setLoading(true);
    setError(null);

    therapistSchedulingServices
      .getSchedulePrintPDFUrl(providerUUID, dateString, sortOrder)
      .then((response: AxiosResponse<SchedulePrintPDFResponse>) => {
        const responseData = response.data;
        if (responseData.code === 'OK' && responseData.message) {
          setPdfUrl(responseData.message);
        } else {
          setError('Failed to fetch PDF URL.');
        }
      })
      .catch(() => setError('An error occurred while fetching the PDF URL.'))
      .finally(() => setLoading(false));
  }, [providerUUID, dateString, sortOrder]);

  useEffect(() => {
    if (open) {
      fetchPdf();
    }
  }, [fetchPdf, open, sortOrder]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>Schedule Print Preview</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            textAlign="center"
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
          >
            <Typography style={{ color: 'red', marginBottom: '16px' }}>{error}</Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={fetchPdf}
            >
              Retry
            </Button>
          </Box>
        ) : pdfUrl ? (
          <iframe
            src={pdfUrl}
            title="Schedule PDF"
            style={{ width: '100%', height: '80vh', border: 'none' }}
          />
        ) : (
          <div>No PDF URL available.</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SchedulePrintDialog;
